export const MSK_SUPPLEMENTAL_SCHEMA_VERSION = 1;

export const MUSCULOSKELETAL_FORM_ID_PREFIXES = Object.freeze([
  'amputation_conditions',
  'ankle_conditions',
  'back_thoracolumbar_spine_conditions',
  'bones_and_other_skeletal_conditions',
  'elbow_and_forearm_conditions',
  'foot_conditions_including_flatfoot',
  'hand_and_fingers',
  'hip_and_thigh_conditions',
  'knee_and_lower_leg',
  'muscle_injuries',
  'neck_cervical_spine_conditions',
  'osteomyelitis',
  'shoulder_and_arm',
  'temporomandibular_disorders',
  'wrist_conditions',
]);

export const MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS = Object.freeze([
  { key: 'pain', label: 'Pain' },
  { key: 'activeRom', label: 'Active ROM' },
  { key: 'passiveRom', label: 'Passive ROM' },
  { key: 'repetitiveUse', label: 'Repetitive use' },
  { key: 'repeatedUseOverTime', label: 'Repeated use over time' },
  { key: 'flareups', label: 'Flareups' },
  {
    key: 'additionalFactors',
    label: 'Additional factors contributing to disability',
    description:
      'e.g. interfering with standing or sitting, disturbance of locomotion, more or less movement than normal, weakened movement, instability of station, swelling, deformity, atrophy of disuse',
  },
  { key: 'ankylosis', label: 'Ankylosis' },
  { key: 'flailJoints', label: 'Flail joints' },
  { key: 'other', label: 'Other (specify)' },
]);

export const FUNCTIONAL_LOSS_LABELS = Object.freeze(
  MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS.reduce((acc, option) => {
    acc[option.key] = option.label;
    return acc;
  }, {}),
);

export const CANNOT_ESTIMATE_PLACEHOLDER =
  "Please explain why it is not possible to evaluate severity without resorting to mere speculation. The explanation should not be based on an examiner's shortcomings or a general aversion to offering an estimate on issues not directly observed.";

export const MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_PREFIX = '_supplemental.musculoskeletal';

const buildSupplementalFieldId = (suffix) =>
  `${MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_PREFIX}.${suffix}`;

export const MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_DEFINITIONS = Object.freeze({
  [buildSupplementalFieldId('isTakingMedication')]: {
    id: buildSupplementalFieldId('isTakingMedication'),
    label: 'Supplemental Q1: Taking medication',
    field_type: 'yes_no',
  },
  [buildSupplementalFieldId('medicationRelievesSymptoms')]: {
    id: buildSupplementalFieldId('medicationRelievesSymptoms'),
    label: 'Supplemental Q2: Medication relieves symptoms',
    field_type: 'yes_no',
  },
  [buildSupplementalFieldId('canEstimateWithoutMedication')]: {
    id: buildSupplementalFieldId('canEstimateWithoutMedication'),
    label: 'Supplemental Q3: Can estimate without medication',
    field_type: 'yes_no',
  },
  [buildSupplementalFieldId('cannotEstimateExplanation')]: {
    id: buildSupplementalFieldId('cannotEstimateExplanation'),
    label: 'Supplemental Q3 explanation',
    field_type: 'long_text',
  },
  [buildSupplementalFieldId('remarks')]: {
    id: buildSupplementalFieldId('remarks'),
    label: 'Supplemental remarks',
    field_type: 'long_text',
  },
  ...MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS.reduce((acc, option) => {
    const basePath = buildSupplementalFieldId(`functionalLosses.${option.key}`);

    acc[`${basePath}.selected`] = {
      id: `${basePath}.selected`,
      label: `Supplemental functional loss: ${option.label}`,
      field_type: 'checkbox',
    };

    acc[`${basePath}.estimatedLoss`] = {
      id: `${basePath}.estimatedLoss`,
      label: `Supplemental estimated loss: ${option.label}`,
      field_type: 'long_text',
    };

    if (option.key === 'other') {
      acc[`${basePath}.otherLabel`] = {
        id: `${basePath}.otherLabel`,
        label: 'Supplemental functional loss: Other label',
        field_type: 'short_text',
      };
    }

    return acc;
  }, {}),
});

export const isMusculoskeletalSupplementalFieldId = (fieldId) =>
  String(fieldId || '').trim().startsWith(`${MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_PREFIX}.`);

export const getMusculoskeletalSupplementalFieldDefinition = (fieldId) =>
  MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_DEFINITIONS[String(fieldId || '').trim()] || null;

export const getMusculoskeletalSupplementalFieldValue = (data, fieldId) => {
  if (!isMusculoskeletalSupplementalFieldId(fieldId)) return undefined;

  const normalized = normalizeMusculoskeletalSupplementalData(data);
  const segments = String(fieldId)
    .trim()
    .split('.')
    .slice(2);

  let current = normalized;
  for (const segment of segments) {
    if (current === null || current === undefined) return undefined;
    current = current?.[segment];
  }

  return current;
};

const normalizeBooleanAnswer = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

const normalizeTextAnswer = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const createDefaultFunctionalLosses = () =>
  MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS.reduce((acc, option) => {
    acc[option.key] =
      option.key === 'other'
        ? { selected: false, otherLabel: '', estimatedLoss: '' }
        : { selected: false, estimatedLoss: '' };
    return acc;
  }, {});

export const createDefaultMusculoskeletalSupplementalData = () => ({
  schemaVersion: MSK_SUPPLEMENTAL_SCHEMA_VERSION,
  isTakingMedication: null,
  medicationRelievesSymptoms: null,
  canEstimateWithoutMedication: null,
  cannotEstimateExplanation: '',
  functionalLosses: createDefaultFunctionalLosses(),
  remarks: '',
});

export const normalizeMusculoskeletalSupplementalData = (data) => {
  const defaults = createDefaultMusculoskeletalSupplementalData();
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return defaults;
  }

  const normalized = {
    ...defaults,
    schemaVersion: MSK_SUPPLEMENTAL_SCHEMA_VERSION,
    isTakingMedication: normalizeBooleanAnswer(data.isTakingMedication),
    medicationRelievesSymptoms: normalizeBooleanAnswer(data.medicationRelievesSymptoms),
    canEstimateWithoutMedication: normalizeBooleanAnswer(data.canEstimateWithoutMedication),
    cannotEstimateExplanation: normalizeTextAnswer(data.cannotEstimateExplanation),
    remarks: normalizeTextAnswer(data.remarks),
    functionalLosses: createDefaultFunctionalLosses(),
  };

  const inputLosses =
    data.functionalLosses && typeof data.functionalLosses === 'object' && !Array.isArray(data.functionalLosses)
      ? data.functionalLosses
      : {};

  for (const option of MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS) {
    const inputLoss = inputLosses[option.key];
    if (!inputLoss || typeof inputLoss !== 'object' || Array.isArray(inputLoss)) {
      continue;
    }

    normalized.functionalLosses[option.key] =
      option.key === 'other'
        ? {
            selected: Boolean(inputLoss.selected),
            otherLabel: normalizeTextAnswer(inputLoss.otherLabel),
            estimatedLoss: normalizeTextAnswer(inputLoss.estimatedLoss),
          }
        : {
            selected: Boolean(inputLoss.selected),
            estimatedLoss: normalizeTextAnswer(inputLoss.estimatedLoss),
          };
  }

  return normalized;
};

export const isMusculoskeletalContentionPath = (value) => {
  const root = String(value || '')
    .split(' > ')[0]
    .trim()
    .toLowerCase();
  return root === 'musculoskeletal';
};

export const hasMusculoskeletalContentions = (request = {}) => {
  const requestContentions = Array.isArray(request?.requestContentions)
    ? request.requestContentions
    : [];

  if (
    requestContentions.some((requestContention) =>
      isMusculoskeletalContentionPath(
        requestContention?.contention || requestContention?.contentionKey || '',
      ),
    )
  ) {
    return true;
  }

  const legacyContentions = String(request?.contention || '')
    .split(/[\n,|]+/)
    .map((segment) => String(segment || '').trim())
    .filter(Boolean);

  return legacyContentions.some(isMusculoskeletalContentionPath);
};

export const isMusculoskeletalDbq = (userDbq = {}) => {
  if (hasMusculoskeletalContentions(userDbq?.request || {})) {
    return true;
  }

  const formId = String(userDbq?.formId || '').trim().toLowerCase();
  if (
    formId &&
    MUSCULOSKELETAL_FORM_ID_PREFIXES.some((prefix) => formId.startsWith(prefix))
  ) {
    return true;
  }

  return false;
};

export const validateMusculoskeletalSupplementalData = (data) => {
  const errors = [];
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    errors.push('Supplemental musculoskeletal data is required.');
    return errors;
  }

  const normalized = normalizeMusculoskeletalSupplementalData(data);

  if (normalized.isTakingMedication === null) {
    errors.push('Question 1 (medication) is required.');
  }

  if (normalized.isTakingMedication !== true) {
    return errors;
  }

  if (normalized.medicationRelievesSymptoms === null) {
    errors.push('Question 2 (medication relieves symptoms) is required.');
  }

  if (normalized.medicationRelievesSymptoms !== true) {
    return errors;
  }

  if (normalized.canEstimateWithoutMedication === null) {
    errors.push('Question 3 (can estimate without medication) is required.');
  }

  if (normalized.canEstimateWithoutMedication === false) {
    if (!normalized.cannotEstimateExplanation.trim()) {
      errors.push(
        'An explanation is required when the examiner cannot estimate severity without medication.',
      );
    }
    return errors;
  }

  if (normalized.canEstimateWithoutMedication !== true) {
    return errors;
  }

  for (const option of MUSCULOSKELETAL_FUNCTIONAL_LOSS_OPTIONS) {
    const value = normalized.functionalLosses?.[option.key];
    if (!value?.selected) continue;

    if (!String(value.estimatedLoss || '').trim()) {
      errors.push(`Estimated functional loss is required for "${FUNCTIONAL_LOSS_LABELS[option.key]}".`);
    }

    if (option.key === 'other' && !String(value.otherLabel || '').trim()) {
      errors.push('A label is required for the "Other" functional loss.');
    }
  }

  return errors;
};
